import React, { useEffect, useRef, useState } from "react";
import { Facebook, MessageCircle, Mail, Link as LinkIcon, Check, X as CloseIcon, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/** X (formerly Twitter) branded logo — lucide doesn't ship this. */
const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

async function writeClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

/**
 * ShareButton: renders a button that opens a share dialog with editable caption
 * and platform-specific share targets. Uses backend OG URLs so previews render
 * with dynamic meta tags on WhatsApp / Facebook / X.
 */
export default function ShareButton({
  shareUrl,
  defaultCaption,
  title = "Share",
  variant = "primary", // 'primary' | 'secondary' | 'ghost'
  size = "md", // 'md' | 'sm'
  testId = "share-btn",
  label = "Share",
  trackContext = null, // {slug, share_type: 'gallery'|'photo', photo_id?, bib?}
}) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState(defaultCaption || "");
  const [copied, setCopied] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    setCaption(defaultCaption || "");
  }, [defaultCaption]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const openShare = () => setOpen(true);

  const encodedUrl = encodeURIComponent(shareUrl || "");
  const encodedText = encodeURIComponent(caption || "");

  const targets = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <MessageCircle size={18} />,
      color: "#25D366",
      href: `https://wa.me/?text=${encodeURIComponent(`${caption ? caption + " " : ""}${shareUrl}`)}`,
      note: "Custom text supported",
    },
    {
      key: "x",
      label: "X",
      icon: <XLogo size={16} />,
      color: "#000000",
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      note: "Custom text supported",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: <Facebook size={18} />,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      note: "Preview auto-generated",
    },
    {
      key: "email",
      label: "Email",
      icon: <Mail size={18} />,
      color: "#475569",
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${caption || ""}\n\n${shareUrl}`)}`,
      note: "Custom text supported",
    },
  ];

  const handleCopy = async () => {
    const payload = caption ? `${caption} ${shareUrl}` : shareUrl;
    const ok = await writeClipboard(payload);
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      trackShare("copy");
      setTimeout(() => setCopied(false), 1800);
    } else {
      window.prompt("Copy this link:", payload);
    }
  };

  const trackShare = (target) => {
    if (!trackContext?.slug) return;
    // Fire-and-forget — never block the share intent
    api
      .post(`/public/events/${trackContext.slug}/track`, {
        kind: "share",
        target,
        share_type: trackContext.share_type || "gallery",
        photo_id: trackContext.photo_id || null,
        bib: trackContext.bib || null,
      })
      .catch(() => {});
  };

  const buttonClass = {
    primary: "bg-[#5B4CFF] hover:bg-[#4a3dde] text-white",
    secondary: "bg-[#0F172A] hover:bg-black text-white",
    ghost: "bg-white text-[#0F172A] border border-[#E2E8F0] hover:border-[#5B4CFF]",
  }[variant] || "bg-[#5B4CFF] hover:bg-[#4a3dde] text-white";

  const sizeClass = size === "sm" ? "px-4 py-2 text-xs" : "px-5 py-3 text-sm";

  return (
    <>
      <button
        onClick={openShare}
        className={`inline-flex items-center gap-2 font-semibold transition-colors ${buttonClass} ${sizeClass}`}
        data-testid={testId}
      >
        <Share2 size={size === "sm" ? 14 : 16} /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="bg-white border border-[#E2E8F0] w-full max-w-lg p-6 sm:p-8 rounded-t-2xl sm:rounded-none max-h-[92vh] overflow-y-auto rs-slide-up sm:rs-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle — mobile only */}
            <div className="sm:hidden flex justify-center -mt-2 mb-4">
              <span className="w-10 h-1.5 bg-[#E2E8F0] rounded-full" />
            </div>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="kicker mb-1">SHARE</p>
                <h3 className="font-display text-xl font-bold text-[#0F172A]">{title}</h3>
              </div>
              <button
                ref={closeRef}
                onClick={() => setOpen(false)}
                className="text-[#475569] hover:text-[#0F172A] p-1 -mr-1 -mt-1"
                aria-label="Close share dialog"
                data-testid="share-close-btn"
              >
                <CloseIcon size={22} />
              </button>
            </div>

            <label className="block text-[10px] font-mono tracking-widest text-[#475569] mb-2">
              CAPTION · EDIT BEFORE SHARING
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              maxLength={280}
              placeholder="Add a message…"
              className="w-full bg-white border border-[#E2E8F0] px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B4CFF] resize-none"
              data-testid="share-caption-input"
            />
            <div className="flex justify-between items-center mt-1 mb-5">
              <p className="text-[11px] text-[#475569]">{caption.length}/280 characters</p>
              <button
                type="button"
                onClick={() => setCaption(defaultCaption || "")}
                className="text-[11px] text-[#5B4CFF] hover:underline"
                data-testid="share-reset-caption"
              >
                Reset
              </button>
            </div>

            <label className="block text-[10px] font-mono tracking-widest text-[#475569] mb-2">SHARE LINK</label>
            <div className="flex gap-2 mb-6">
              <input
                readOnly
                value={shareUrl || ""}
                className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-2.5 text-xs font-mono text-[#0F172A] focus:outline-none"
                onFocus={(e) => e.target.select()}
                data-testid="share-url-input"
              />
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] px-3 py-2.5 text-xs font-semibold text-[#0F172A] transition-colors"
                data-testid="share-copy-btn"
              >
                {copied ? <Check size={14} className="text-[#10B981]" /> : <LinkIcon size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <p className="kicker mb-3">SHARE TO</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
              {targets.map((t) => (
                <a
                  key={t.key}
                  href={t.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackShare(t.key);
                    setTimeout(() => setOpen(false), 200);
                  }}
                  className="flex flex-col items-center justify-center gap-2 border border-[#E2E8F0] hover:border-[#5B4CFF] py-4 transition-colors"
                  data-testid={`share-target-${t.key}`}
                >
                  <span style={{ color: t.color }}>{t.icon}</span>
                  <span className="text-xs font-semibold text-[#0F172A]">{t.label}</span>
                </a>
              ))}
            </div>
            <p className="text-[11px] text-[#475569] leading-relaxed mt-3">
              Facebook renders the preview from the link&apos;s meta tags, so your caption may not appear there.
              WhatsApp, X and Email attach your custom caption directly.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
